import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formData);
      
      setSubmitStatus({
        type: 'success',
        message: response.data.message
      });
      
      // Clear form
      setFormData({ name: '', email: '', company: '', message: '' });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 5000);
      
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.detail || 'Failed to send message. Please try again or email us directly at office@nyaxo.net'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 px-6 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Contact Info */}
          <div data-testid="contact-info">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase tracking-wide">
              Let's <span className="text-[#00E600]">Connect</span>
            </h2>
            <p className="text-[#B3B3B3] text-lg mb-8 leading-relaxed">
              Ready to transform your business with AI? Our team of experts is here to help you navigate your digital transformation journey.
            </p>

            {/* Office Location */}
            <div className="space-y-6">
              <div className="flex items-start space-x-4" data-testid="office-location">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-2">Singapore Office</h3>
                  <p className="text-[#B3B3B3] leading-relaxed">
                    10 ANSON ROAD #33-10C<br />
                    SINGAPORE 079903
                  </p>
                  <p className="text-[#00E600] text-sm mt-2 font-semibold">Global AI Innovation Hub</p>
                </div>
              </div>

              <div className="flex items-start space-x-4" data-testid="contact-email">
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-[#00E600] to-[#36E0A8] rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-[#0D0D0D]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-white font-semibold text-lg mb-1">Email</h3>
                  <a href="mailto:office@nyaxo.net" className="text-[#00E600] hover:text-[#36E0A8] transition-colors">
                    office@nyaxo.net
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div data-testid="contact-form-container" className="bg-[#1A1A1A]/60 backdrop-blur-sm border border-[#2F2F2F] rounded-lg p-6 md:p-8">
            {/* Status Messages */}
            {submitStatus.message && (
              <div className={`mb-6 p-4 rounded-md ${
                submitStatus.type === 'success' 
                  ? 'bg-[#00E600]/10 border border-[#00E600] text-[#00E600]' 
                  : 'bg-red-500/10 border border-red-500 text-red-500'
              }`}>
                <p className="text-sm font-semibold">{submitStatus.message}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-white mb-2 font-semibold">Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    data-testid="contact-form-name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full bg-[#0D0D0D] border border-[#2F2F2F] rounded-md px-4 py-3 text-base md:text-sm text-white focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors disabled:opacity-50"
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-white mb-2 font-semibold">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    data-testid="contact-form-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    className="w-full bg-[#0D0D0D] border border-[#2F2F2F] rounded-md px-4 py-3 text-base md:text-sm text-white focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors disabled:opacity-50"
                    placeholder="your.email@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-white mb-2 font-semibold">Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    data-testid="contact-form-company"
                    value={formData.company}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full bg-[#0D0D0D] border border-[#2F2F2F] rounded-md px-4 py-3 text-base md:text-sm text-white focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors disabled:opacity-50"
                    placeholder="Your company"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-white mb-2 font-semibold">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-form-message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    disabled={isSubmitting}
                    rows="4"
                    className="w-full bg-[#0D0D0D] border border-[#2F2F2F] rounded-md px-4 py-3 text-base md:text-sm text-white focus:border-[#00E600] focus:outline-none focus:ring-1 focus:ring-[#00E600] transition-colors resize-none disabled:opacity-50"
                    placeholder="Tell us about your project"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  data-testid="contact-form-submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#00E600] to-[#36E0A8] text-[#0D0D0D] font-bold py-3 md:py-4 rounded-md hover:shadow-[0_0_30px_rgba(0,230,0,0.6)] transition-all duration-300 uppercase tracking-wide text-sm md:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#0D0D0D]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;